import React from 'react';
import lodash from 'lodash';
import TouchableOpacity from './TouchableOpacity';
import { RiMapPinFill } from 'react-icons/ri';
import Text from './Text';
import RedcatImage from './RedcatImage';
import { BRAND_ICON_PREFIX } from '../constants';
import { useAppSelector } from '../app/hooks';
import moment from 'moment';

import {
  OrderingConstants,
  OrderingSelectors,
  getTimeFromUTCOffset,
  getOpensAtText,
  getScheduleFromText,
  getOpeningHoursRangeText,
} from 'polygon-ordering';
import getThemeLookup from '../selectors/getThemeLookup';
const { getKeyOrderPropertyUpdateInProgress, getBufferSaleType } = OrderingSelectors;
import { useTranslation } from 'react-i18next';
import combineStyles from '../utils/combineStyles';
import { CONTAINER_PROPERTIES, TEXT_PROPERTIES } from '../utils/theme';
import { storeSelected } from '../utils/analytics';

const { DELIVERY_SALE_TYPES, SALE_TYPE } = OrderingConstants;

const LocationListItem = ({
  location,
  onClick,
  activateBgColor,
}: {
  location: MapLocation;
  onClick?: () => void;
  activateBgColor: boolean;
}) => {
  const p = useAppSelector(getThemeLookup);
  const { t } = useTranslation();

  const keyOrderPropertyUpdateInProgress = useAppSelector(getKeyOrderPropertyUpdateInProgress);
  const modalParams = useAppSelector(state => state.modalParams);

  // Store utc
  const storeUtc = location?.utcOffset
    ? getTimeFromUTCOffset(location.utcOffset)
    : moment().format('Z');

  // Getting location opening and closing time based on current sale type
  const modalSaleType = modalParams?.saleType;
  const bufferSaleType = useAppSelector(getBufferSaleType);
  const saleType = modalSaleType || bufferSaleType;

  const { openingTime: LocationOT, closingTime: LocationCT } =
    location.availableSaleTypesToday[String(saleType)];

  // Location closed if current time is NOT between opening and closing time
  const isLocationClosed = !moment().isBetween(LocationOT, LocationCT);
  // Next opening time of location either today or tomorrow depending on user current time
  const locationNextOpensAt = moment().isBefore(LocationOT)
    ? LocationOT
    : location.openingTimeTomorrow || LocationOT;

  const isOpeningBetweenNowAndClosing = moment(LocationOT).isBetween(moment(), LocationCT);

  const minutesLeftToClosing = Math.abs(moment().utcOffset(storeUtc).diff(LocationCT, 'minutes'));
  const openingHoursRangeText = getOpeningHoursRangeText({
    openingTime: LocationOT,
    closingTime: LocationCT,
    utcOffset: location?.utcOffset,
  });

  const opensAtText = getOpensAtText({
    time: locationNextOpensAt,
    utcOffset: location?.utcOffset,
    label: t('locationOpensAt'),
  });

  const scheduleFromText = getScheduleFromText({
    time: locationNextOpensAt,
    utcOffset: location?.utcOffset,
    isOpeningBetweenNowAndClosing,
    prefix: t('ScheduleFrom'),
    todaySuffix: t('today'),
  });

  const sortedBrands = lodash.sortBy(location.brands, 'sortOrder');

  const isCateringPickup = modalParams?.saleType === SALE_TYPE.CATERING;
  const isCateringDelivery = modalParams?.saleType === SALE_TYPE.CATERING_DELIVERY;

  const takingOrders = isCateringPickup
    ? true
    : location.online && location.takingOrders?.includes(saleType);

  const isIgnoredLocationDisabledSaleTypes = DELIVERY_SALE_TYPES.includes(saleType);

  const isLocationDisabled = isIgnoredLocationDisabledSaleTypes
    ? false
    : !takingOrders || keyOrderPropertyUpdateInProgress;

  const onHandleClick = () => {
    if (onClick) {
      onClick();
    }
    storeSelected({
      storeName: location.name,
      saleType,
    });
  };

  return (
    <TouchableOpacity
      disabled={isLocationDisabled}
      key={location.id}
      style={combineStyles(
        p('locationItem', CONTAINER_PROPERTIES),
        p('locationListItem', ['border']),
        {
          padding: '1em 1em',
          borderRadius: 8,
          margin: 10,
          backgroundColor: activateBgColor
            ? p('activeLocationListItem', ['backgroundColor']).backgroundColor ||
              p('locationItemSelected', ['backgroundColor']).backgroundColor
            : p('locationItem', ['backgroundColor']).backgroundColor,
          transition: '0.25s all cubic-bezier(0.64, 0.01, 0.32, 0.97)',
        },
        activateBgColor && p('locationListItemActive', ['border']),
      )}
      onClick={onHandleClick}
    >
      <div
        style={{
          display: 'flex',
          opacity: isLocationDisabled ? 0.4 : 1,
        }}
      >
        <div style={{ flex: 1 }}>
          <Text style={{ fontWeight: 500 }} value={location.name} /> <br />
          <Text value={(location.address.combinedShort || '').trim()} themeKey="shortAddress" />
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            {(sortedBrands || []).map((brand: any, index) => (
              <div style={{ marginTop: 5 }} key={index}>
                <RedcatImage
                  containerStyle={styles.brandImage}
                  imagePath={brand.imagePath}
                  alt={brand.name}
                  size={25}
                  pathPrefix={BRAND_ICON_PREFIX}
                />
              </div>
            ))}
          </div>
          <div>
            {!isIgnoredLocationDisabledSaleTypes && !takingOrders && (
              <Text
                style={combineStyles([
                  { color: '#f44336', fontSize: 12 },
                  p('closedLocation', TEXT_PROPERTIES),
                ])}
              >
                {isLocationDisabled && isLocationClosed
                  ? opensAtText
                  : `${t('locationNotTakingOrders')}`}
              </Text>
            )}
            {takingOrders && minutesLeftToClosing < 60 && !isLocationClosed && (
              <Text style={{ color: '#f44336', fontSize: 12 }}>Closing soon </Text>
            )}
            {takingOrders && isLocationClosed && (
              <Text
                style={{
                  color: isOpeningBetweenNowAndClosing ? '#9e9e9e' : '#f44336',
                  fontSize: 12,
                }}
              >
                {scheduleFromText}
              </Text>
            )}
            {takingOrders && !isLocationClosed && (
              <Text
                style={{
                  fontSize: 12,
                  color: '#9e9e9e',
                  marginLeft: minutesLeftToClosing < 60 ? 5 : 0,
                }}
              >
                {openingHoursRangeText}
              </Text>
            )}
          </div>
          {!!location.bestDeliveryTime && (
            <Text themeKey="locationListItemBestDeliveryTime">
              {t('deliveryExpectedLabel') +
                moment(location.bestDeliveryTime).format(
                  isCateringDelivery ? 'ddd, D MMM, YY h:mm a' : 'LT',
                )}
            </Text>
          )}
        </div>

        <div
          className="ml-3"
          style={{
            flexDirection: 'column',
            display: 'flex',
            gap: '0.5em',
            justifyContent: 'center',
          }}
        >
          {!!location.distance && (
            <div
              style={{
                alignSelf: 'center',
                flexDirection: 'column',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25em',
                width: 45,
              }}
            >
              <RiMapPinFill
                size={20}
                color={p('mapSearchResultIcon', ['color']).color || 'indianred'}
              />
              <Text
                value={
                  location.distance < 1
                    ? (location.distance * 1000).toFixed(0) + ' m'
                    : location.distance.toFixed(1) + ' km'
                }
                style={{ fontSize: 12 }}
              />
            </div>
          )}
        </div>
      </div>
    </TouchableOpacity>
  );
};

export default LocationListItem;

const styles = {
  brandImage: {
    marginRight: 10,
    borderRadius: 5,
    overflow: 'hidden',
  },
};
